import { useMemo } from 'react';

import { useOptimizationPipeline } from '../OptimizationPipelineProvider';

export default function UnitDetailsIndicator() {
    const { mergedMeasurementDataList, mergedMeasurementData, ts } = useOptimizationPipeline();

    const rows = useMemo(() => {
        if (!Array.isArray(mergedMeasurementDataList) || !ts) return [];

        const target = (ts instanceof Date) ? ts.getTime() : new Date(ts).getTime();
        if (Number.isNaN(target)) return [];

        const entry = mergedMeasurementDataList.find(e => {
            const t = e?.ts ? new Date(e.ts).getTime() : NaN;
            return !Number.isNaN(t) && t === target;
        });

        if (!entry) return [];

        const fallbackLabel = (mergedMeasurementData && mergedMeasurementData.name) ? mergedMeasurementData.name : 'Measurement';
        const fallbackState = entry?.meta?.synthetic_data === true ? 'SYNTH' : 'MEASURED';
        const fallbackRows = [{ key: fallbackLabel, state: fallbackState }];

        const meta = entry?.meta;
        if (!meta || typeof meta !== 'object') return fallbackRows;

        // try the plain merger key
        let mergerObj = meta.merger;

        // fallback to legacy merger: keys
        if (!mergerObj) {
            const mergerKey = Object.keys(meta).find(k => k.startsWith('merger:'));
            if (mergerKey) mergerObj = meta[mergerKey];
        }

        const inputsObj = (mergerObj && typeof mergerObj === 'object') ? mergerObj.inputs : null;

        if (!inputsObj || typeof inputsObj !== 'object') return fallbackRows;

        const inputRows = Object.entries(inputsObj).map(([key, val]) => {
            const synth = val?.synthetic_data;

            if (synth === true) return { key, state: 'SYNTH' };
            if (synth === false) return { key, state: 'MEASURED' };

            // if inputs only have timestamps/origin treat as measured
            if (val && typeof val === 'object' && (val.timestamp || val.original_timestamp || val.data_origin))
                return { key, state: 'MEASURED' };

            return { key, state: 'MISSING' };
        });

        return inputRows.length ? inputRows : fallbackRows;
    }, [mergedMeasurementDataList, mergedMeasurementData, ts]);

    const stateBadge = (state) => {
        switch (state) {
            case 'SYNTH':
                return (<div className='w-full lg:w-auto text-center font-semibold px-4 py-2 rounded-full lg:rounded bg-amber-100 text-amber-900'>Synthetic</div>);
            case 'MEASURED':
                return (<div className='w-full lg:w-auto text-center font-semibold px-4 py-2 rounded-full lg:rounded bg-green-100 text-green-900'>Measured</div>);
            default:
                return (<div className='w-full lg:w-auto text-center font-semibold px-4 py-2 rounded-full lg:rounded bg-gray-100 text-gray-500 opacity-70'>Missing</div>);
        }
    };

    return (
        <div className='w-full flex flex-col gap-2 shadow-inner p-4 bg-white rounded-xl border text-11'>
            {rows.length ? rows.map((row, i) => (
                <div key={i} className='flex flex-col lg:flex-row items-center justify-between gap-4 border-b last:border-0 px-4 py-4 lg:py-2'>
                    <span className='text-10 text-center lg:text-left'>{row.key}</span>
                    {stateBadge(row.state)}
                </div>
            )) : (<div className='px-8 py-1 rounded-lg bg-gray-200 opacity-50'>No Input</div>)}
        </div>
    );
}
